import { useEffect, useRef } from 'react'

/**
 * Agrupa los efectos de configuración automática del tenant:
 *
 * 1. Valida que el tenant seleccionado pertenezca al cantón actual; si no, lo resetea.
 *
 * 2. Inicialización por cantón (carga / entrada por URL): 1 tenant real -> auto-seleccionarlo;
 *    >1 tenant real -> "Todos". Excluye el tenant genérico/temporal. Una vez por cantón.
 *
 * 3. Para usuarios con acceso restringido (allowedCantonSlugs !== null) en rutas
 *    protegidas, auto-selecciona el tenant asignado al cambiar de cantón o en carga inicial.
 *
 * NOTA: Pasar `disabled = true` en páginas de edición de árbol, donde el formulario
 * gestiona la selección de tenant de forma independiente (reasignación cross-cantón).
 */
export const useTenantAutoConfig = ({
  selectedTenant,
  citiesLoading,
  currentTenants,
  resetTenant,
  currentCity,
  allowedCantonSlugs,
  isOnProtectedRoute,
  statusBarTenants,
  selectTenant,
  getAssignedTenantsForCanton,
  disabled = false,
}) => {
  // Validar que el tenant seleccionado sea válido para el cantón actual.
  // Deshabilitado en el formulario de edición: el usuario puede reasignar el árbol
  // a un cantón diferente al de la URL, y LocationStep gestiona el tenant de forma independiente.
  useEffect(() => {
    if (disabled || !selectedTenant || citiesLoading) return
    const isValidForCanton = currentTenants.some(t => t.slug === selectedTenant)
    if (!isValidForCanton && currentTenants.length > 0) {
      resetTenant()
    }
  }, [disabled, currentCity, currentTenants, selectedTenant, resetTenant, citiesLoading])

  // Regla de inicialización del tenant por cantón (al cargar la app o entrar por URL):
  //  - Si el cantón tiene 1 solo tenant real -> auto-seleccionarlo.
  //  - Si tiene más de 1 tenant real -> "Todos" (null).
  // "Tenant real" excluye el genérico/temporal (ÁrbolEc), que aparece en todos los
  // cantones al estar logueado y no debe contar para esta decisión.
  // Se aplica una sola vez por cantón para no pisar la selección manual posterior del usuario.
  const appliedForCantonRef = useRef(null)
  useEffect(() => {
    if (disabled || citiesLoading || !currentCity) return
    const realTenants = statusBarTenants.filter(t => !t.isGeneric)
    if (realTenants.length === 0) return // tenants del cantón aún no cargados
    if (appliedForCantonRef.current === currentCity) return
    appliedForCantonRef.current = currentCity

    if (realTenants.length === 1) {
      if (selectedTenant !== realTenants[0].slug) selectTenant(realTenants[0].slug)
    } else if (selectedTenant !== null) {
      resetTenant()
    }
  }, [disabled, citiesLoading, currentCity, statusBarTenants, selectedTenant, selectTenant, resetTenant])

  // Auto-seleccionar tenant para usuarios con acceso restringido al cambiar de cantón o en carga inicial
  useEffect(() => {
    if (disabled || !allowedCantonSlugs || !isOnProtectedRoute) return
    if (selectedTenant) {
      const validForCanton = statusBarTenants.some(t => t.slug === selectedTenant)
      if (validForCanton) return
    }

    if (statusBarTenants.length === 1) {
      selectTenant(statusBarTenants[0].slug)
    } else if (statusBarTenants.length > 1) {
      const assignedForCanton = getAssignedTenantsForCanton(currentCity)
      const primary = assignedForCanton?.find(t => t.isPrimary)
      if (primary) {
        selectTenant(primary.tenantSlug)
      }
    }
  }, [disabled, currentCity, allowedCantonSlugs, isOnProtectedRoute, statusBarTenants, selectedTenant, selectTenant, getAssignedTenantsForCanton])
}
